import { AppState, User } from '@auth0/auth0-react';
import { ROLES, ROLE_HIERARCHY } from '../config/routeConfig';
import { FeatureFlagsResponse } from '../hooks/useFeatureFlags'; // NEW: For feature flag-based tier detection (Week 4, Task 4.2)

/**
 * Utility functions for smart post-authentication routing
 * 
 * MIGRATION NOTE (Week 4, Task 4.2):
 * - Legacy role-based functions are marked as @deprecated
 * - New feature flag-based functions added:
 *   - getSubscriptionTierFromFeatures() - Determines tier from feature flags
 *   - hasFeatureAccess() - Checks specific feature access
 * - Use feature flag functions for new code
 * - Legacy functions maintained for backward compatibility
 */

// Valid return destinations
const VALID_RETURN_ROUTES = [
  '/dashboard',
  '/pricing',
  '/checkout',
  '/trial-setup',
  '/settings',
  '/bot-maintenance'
];

/**
 * Validates if a returnTo URL is safe and allowed
 */
export const isValidReturnTo = (returnTo: string): boolean => {
  if (!returnTo) return false;
  
  // Check if the route starts with a valid pattern
  return VALID_RETURN_ROUTES.some(route => returnTo.startsWith(route));
};

/**
 * Extracts user roles from Auth0 user object
 */
export const getUserRoles = (user?: User): string[] => {
  if (!user) return [];
  
  const roles = user['https://tradingdashboard.com/roles'] as string[] | undefined;
  return Array.isArray(roles) ? roles : [];
};

/**
 * Checks if user has any subscription role
 */
export const hasValidSubscription = (userRoles: string[]): boolean => {
  return userRoles.some(role => Object.values(ROLES).includes(role));
};

/**
 * Determines if user is a first-time user (new signup)
 */
export const isFirstTimeUser = (user?: User): boolean => {
  if (!user) return true;
  
  // Check for onboarded custom claim
  const isOnboarded = user['https://tradingdashboard.com/onboarded'] as boolean | undefined;
  return !isOnboarded;
};

/**
 * Gets the highest role from user's role hierarchy
 * 
 * @deprecated As of Week 4 Task 4.2 - Use getSubscriptionTierFromFeatures() instead for feature flag-based tier detection.
 * This function will be removed in Week 5.
 * 
 * Migration:
 * ```typescript
 * // Before:
 * const highestRole = getHighestRole(userRoles);
 * 
 * // After:
 * const tier = getSubscriptionTierFromFeatures(featureFlags);
 * ```
 */
export const getHighestRole = (userRoles: string[]): string | null => {
  if (process.env.NODE_ENV === 'development') {
    console.warn('[DEPRECATED] getHighestRole is deprecated. Use getSubscriptionTierFromFeatures() instead.');
  }
  
  if (!userRoles.length) return null;
  
  // Find the highest role in hierarchy
  let highestRoleIndex = -1;
  let highestRole: string | null = null;
  
  userRoles.forEach(role => {
    const roleIndex = ROLE_HIERARCHY.indexOf(role);
    if (roleIndex > highestRoleIndex) {
      highestRoleIndex = roleIndex;
      highestRole = role;
    }
  });
  
  return highestRole;
};

// ============================================================================
// NEW: Feature Flag-Based Utilities (Week 4, Task 4.2)
// ============================================================================

/**
 * Subscription tier type based on feature access
 */
export type SubscriptionTier = 'trial' | 'basic' | 'standard' | 'elite' | 'admin' | 'none';

/**
 * Determines user's subscription tier based on feature flag access
 * This is the new recommended way to detect subscription levels.
 * 
 * Tier Detection Logic:
 * - Elite: Has portfolio_optimization feature
 * - Standard: Has strategy_details OR portfolio_forecast features
 * - Trial: Has dashboard feature
 * - None: No subscription features
 * 
 * @param flags - Feature flags response from useFeatureFlags hook
 * @returns Subscription tier string
 * 
 * @example
 * ```typescript
 * const { data: flags } = useFeatureFlags();
 * const tier = getSubscriptionTierFromFeatures(flags);
 * 
 * if (tier === 'elite') {
 *   // Show elite features
 * }
 * ```
 */
export const getSubscriptionTierFromFeatures = (flags?: FeatureFlagsResponse): SubscriptionTier => {
  if (!flags) return 'none';

  // Check for Admin access (admin_panel feature)
  if (flags['admin_panel']?.enabled === true) {
    return 'admin';
  }

  // Check for Elite-level features (portfolio_optimization)
  if (flags['portfolio_optimization']?.enabled === true) {
    return 'elite';
  }

  // Check for Standard-level features (strategy_details or portfolio_forecast)
  if (flags['strategy_details']?.enabled === true || 
      flags['portfolio_forecast']?.enabled === true) {
    return 'standard';
  }

  // Check for Trial-level features (dashboard access)
  if (flags['dashboard']?.enabled === true) {
    return 'trial';
  }

  return 'none';
};

/**
 * Checks if user has access to a specific feature
 * 
 * @param flags - Feature flags response
 * @param featureKey - Feature key to check
 * @returns true if feature is enabled, false otherwise
 * 
 * @example
 * ```typescript
 * const { data: flags } = useFeatureFlags();
 * const canOptimize = hasFeatureAccess(flags, 'portfolio_optimization');
 * ```
 */
export const hasFeatureAccess = (flags: FeatureFlagsResponse | undefined, featureKey: string): boolean => {
  if (!flags) return false;
  return flags[featureKey]?.enabled === true;
};

/**
 * Checks if user has any subscription-level access based on feature flags
 * 
 * @param flags - Feature flags response
 * @returns true if user has trial or higher access
 */
export const hasValidSubscriptionFromFeatures = (flags?: FeatureFlagsResponse): boolean => {
  const tier = getSubscriptionTierFromFeatures(flags);
  return tier !== 'none';
};

/**
 * Compares two subscription tiers
 * 
 * @param tier1 - First tier to compare
 * @param tier2 - Second tier to compare
 * @returns positive if tier1 > tier2, negative if tier1 < tier2, 0 if equal
 * 
 * @example
 * ```typescript
 * if (compareSubscriptionTiers('elite', 'standard') > 0) {
 *   console.log('Elite is higher than Standard');
 * }
 * ```
 */
export const compareSubscriptionTiers = (tier1: SubscriptionTier, tier2: SubscriptionTier): number => {
  const tierHierarchy: Record<SubscriptionTier, number> = {
    'none': -1,
    'trial': 0,
    'basic': 1,
    'standard': 2,
    'elite': 3,
    'admin': 4
  };
  
  return tierHierarchy[tier1] - tierHierarchy[tier2];
};

/**
 * Main smart routing logic - determines where to send user after authentication
 */
export const determinePostAuthRoute = (
  user?: User, 
  appState?: AppState, 
  userRoles: string[] = []
): string => {
  console.log('[AuthRouting] Determining post-auth route:', {
    hasUser: !!user,
    appState,
    userRoles,
    isFirstTime: isFirstTimeUser(user)
  });

  // 1. Honor explicit returnTo if valid and provided
  if (appState?.returnTo && isValidReturnTo(appState.returnTo)) {
    console.log('[AuthRouting] Using explicit returnTo:', appState.returnTo);
    return appState.returnTo;
  }

  // 2. Handle auto-trial intent (already handled in Auth0ProviderWithNavigate)
  if (appState?.autoStartTrial && appState?.intent === 'trial_direct') {
    console.log('[AuthRouting] Auto-trial intent detected, routing to trial-setup');
    return '/trial-setup';
  }

  // 3. Handle new users (no subscription role yet)
  if (!hasValidSubscription(userRoles)) {
    // If returnTo starts with /checkout, preserve it for new users (keeps plan and discount code)
    if (appState?.returnTo?.startsWith('/checkout')) {
      console.log('[AuthRouting] New user with checkout intent, preserving returnTo:', appState.returnTo);
      return appState.returnTo;
    }
    console.log('[AuthRouting] New user without subscription, routing to trial signup');
    return '/checkout?plan=trial';
  }

  // 4. Handle users with trial subscription
  if (userRoles.includes(ROLES.TRIAL)) {
    console.log('[AuthRouting] Trial user, routing to dashboard');
    return '/dashboard';
  }

  // 5. Handle users with paid subscription
  if (userRoles.some(role => [ROLES.BASIC, ROLES.STANDARD, ROLES.ELITE, ROLES.ADMIN].includes(role))) {
    console.log('[AuthRouting] Paid user, routing to dashboard');
    return '/dashboard';
  }

  // 6. Fallback for edge cases
  console.log('[AuthRouting] Fallback routing to pricing');
  return '/pricing';
};

/**
 * Creates enhanced appState for authentication with intent tracking
 */
export const createAuthIntent = {
  trial: () => ({
    returnTo: '/checkout?plan=trial',
    intent: 'trial',
    autoStartTrial: false
  }),
  
  trialDirect: () => ({
    returnTo: '/trial-setup',
    intent: 'trial_direct',
    autoStartTrial: true
  }),
  
  login: () => ({
    returnTo: '/dashboard',
    intent: 'login',
    autoStartTrial: false
  }),
  
  checkout: (planId: string) => ({
    returnTo: `/checkout?plan=${planId}`,
    intent: 'purchase',
    autoStartTrial: false
  }),
  
  custom: (returnTo: string, intent: string) => ({
    returnTo,
    intent,
    autoStartTrial: false
  })
};

export default {
  // Legacy role-based functions (deprecated)
  isValidReturnTo,
  getUserRoles,
  hasValidSubscription,
  isFirstTimeUser,
  getHighestRole, // @deprecated - Use getSubscriptionTierFromFeatures
  determinePostAuthRoute,
  createAuthIntent,
  
  // NEW: Feature flag-based functions (Week 4, Task 4.2)
  getSubscriptionTierFromFeatures,
  hasFeatureAccess,
  hasValidSubscriptionFromFeatures,
  compareSubscriptionTiers
};